import React, { useCallback, useEffect, useState } from "react";
import OftadehLayout from "../../../components/OftadehLayout/OftadehLayout";
import { Button } from "@material-ui/core";
import Icon from "@material-ui/core/Icon";
import { observer } from "mobx-react-lite";
import { useIsMounted } from "../../../helpers/useIsMounted";
import { useStore } from "../../../stores/helpers/useStore";
import HostsTable from "./HostsTable";
import ConfirmationModal from "../../../components/Modals/ConfirmationModal/ConfirmationModal";
import ErrorModal from "../../../components/Modals/ErrorModal/ErrorModal";
import "./HostsList.css";

const HostsList = (props) => {
	const { history } = props;
	const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
	const [deletingError, setDeletingError] = useState("");
	const [submittingRequest, setSubmittingRequest] = useState(false);
	const [hostToDelete, setHostToDelete] = useState({});
	const isMounted = useIsMounted();

	const {
		hostsStore: { hostsList, haveFreeSlot, getListOfHosts, deleteHost },
		subscriptionStore: {
			subscriptionStatus,
			subscriptionQuantity,
			getSubscriptionInfo,
			easterEggActive,
		},
		uiStore: { loading },
	} = useStore();

	// Get effective subscription status considering easter egg
	const effectiveStatus = easterEggActive ? "ACTIVE" : subscriptionStatus;
	const effectiveQuantity = easterEggActive ? 10 : subscriptionQuantity;

	const getSubscription = useCallback(async () => {
		try {
			await getSubscriptionInfo();
		} catch {
			history.push("/pages/errors/error-500");
		}
	}, [getSubscriptionInfo, history]);

	const deletePlayer = (host) => {
		setHostToDelete(host);
		setOpenConfirmationModal(true);
	};

	const confirm = async () => {
		setSubmittingRequest(true);
		try {
			await deleteHost(hostToDelete.id);
		} catch (err) {
			setDeletingError(
				err.response.data?.message ||
					"Something went wrong! Please try again later!"
			);
		} finally {
			setSubmittingRequest(false);
			setOpenConfirmationModal(false);
		}
	};

	const handleClose = () => {
		setOpenConfirmationModal(false);
		setDeletingError("");
	};

	useEffect(() => {
		getListOfHosts();
	}, [getListOfHosts]);

	useEffect(() => {
		getSubscription();
	}, [getSubscription]);

	return (
		<>
			<OftadehLayout loading={loading || !isMounted}>
				<div className="hosts-header">
					<h1>
						{window.labs.hosts}
					</h1>
					{effectiveQuantity > 0 && (
						<div className="hosts-count">
							{hostsList.length}/{effectiveQuantity}
						</div>
					)}
				</div>

				{effectiveStatus === "INACTIVE" && (
					<div className="info-card" style={{ marginBottom: '20px' }}>
						<div className="info-card-content">
							<div className="info-card-icon">
								<Icon>info</Icon>
							</div>
							<div className="info-card-text">
								<h3>Subscription Required</h3>
								<p>
									{hostsList.length
										? "Your host is linked to this Studio account. Activate a subscription to manage hosts and unlock enterprise features."
										: "To add and manage hosts, you'll need an active subscription."}
								</p>
								<Button
									variant="contained"
									color="primary"
									size="small"
									onClick={() => history.push("/subscription")}
									style={{ marginTop: '10px' }}
								>
									View Subscription Plans
								</Button>
							</div>
						</div>
					</div>
				)}

				{effectiveStatus !== "INACTIVE" ? (
					<>
						{!!hostsList.length ? (
							<div className="hosts-table-container">
								<HostsTable hostsList={hostsList} deletePlayer={deletePlayer} />
							</div>
						) : (
							<div className="empty-state-card">
								<div className="empty-state-content">
									<div className="empty-state-icon">
										<span role="img" aria-label="target">🎯</span>
									</div>
									<h3>Ready to Build Your Team?</h3>
									<p>
										You don't have any hosts yet. Get started by inviting an existing host or creating a new account!
									</p>
								</div>
							</div>
						)}

						<div className="action-buttons-container">
							{haveFreeSlot ? (
								<>
									<Button
										variant="contained"
										color="primary"
										startIcon={<Icon>person_add</Icon>}
										onClick={() => history.push("/invite-host")}
									>
										Invite Host
									</Button>
									<Button
										variant="contained"
										color="primary"
										startIcon={<Icon>add_circle</Icon>}
										onClick={() => history.push("/create-host")}
									>
										Create Host
									</Button>
								</>
							) : (
								<div className="info-card">
									<div className="info-card-content">
										<div className="info-card-icon">
											<span role="img" aria-label="chart">📊</span>
										</div>
										<div className="info-card-text">
											<h3>You've reached your host limit</h3>
											<p>
												Upgrade your subscription to add more hosts and expand your trivia empire!
											</p>
										</div>
									</div>
								</div>
							)}
						</div>

						{!haveFreeSlot && (
							<div className="action-buttons-container">
								<Button
									variant="contained"
									color="primary"
									onClick={() => history.push("/subscription")}
								>
									Update Subscription
								</Button>
							</div>
						)}
					</>
				) : (
					<>
						{/* Show table but disabled */}
						<div style={{ opacity: 0.5, pointerEvents: 'none', position: 'relative' }}>
							{!!hostsList.length ? (
								<div className="hosts-table-container">
									<HostsTable hostsList={hostsList} deletePlayer={deletePlayer} />
								</div>
							) : (
								<div className="empty-state-card">
									<div className="empty-state-content">
										<div className="empty-state-icon">
											<span role="img" aria-label="target">🎯</span>
										</div>
										<h3>No Hosts Yet</h3>
										<p>
											Subscribe to start adding hosts to your trivia empire!
										</p>
									</div>
								</div>
							)}
							
							<div className="action-buttons-container">
								<Button
									variant="contained"
									color="primary"
									startIcon={<Icon>person_add</Icon>}
									disabled
								>
									Invite Host
								</Button>
								<Button
									variant="contained"
									color="primary"
									startIcon={<Icon>add_circle</Icon>}
									disabled
								>
									Create Host
								</Button>
							</div>
						</div>
					</>
				)}
			</OftadehLayout>

			<ConfirmationModal
				title={`You really want to delete ${hostToDelete.roomCode}?`}
				open={openConfirmationModal}
				loading={submittingRequest}
				handleClose={handleClose}
				confirm={confirm}
			>
				If this was created host, it will completely delete his account with all
				players.
				<br />
				If host was invited, it will cancel his <b>PRO</b> status.
			</ConfirmationModal>

			<ErrorModal
				open={!!deletingError}
				title="Deleting of host failed!"
				errorText={deletingError}
				handleClose={handleClose}
			/>
		</>
	);
};

export default observer(HostsList);
